import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table, Button, Spin, message, Tag, Space, Tooltip, InputNumber, Form, Drawer } from 'antd';
import { 
    DollarOutlined, 
    EditOutlined, 
    CheckCircleOutlined, 
    CloseCircleOutlined,
    ReloadOutlined,
    EyeOutlined,
    CheckOutlined
} from '@ant-design/icons';
import { FaFileInvoiceDollar, FaMoneyBillWave } from 'react-icons/fa';
import dayjs from 'dayjs';
import drEasyApi from '../../../Api/Customapi';
import { getImg } from '../../../Api/getImg';

const PatientInvoiceHistoryModal = ({ 
    visible, 
    onCancel, 
    patientId,
    patientName,
    isMobile,
    onEditInvoice,
    onPayInvoice
}) => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: isMobile ? 5 : 10,
        total: 0
    });
    const [isPaidFilter, setIsPaidFilter] = useState(undefined);
    const [payModalVisible, setPayModalVisible] = useState(false);
    const [currentInvoice, setCurrentInvoice] = useState(null);
    const [payForm] = Form.useForm();
    const [paying, setPaying] = useState(false);
    const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
    const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState(null);

    // Fetch invoices
    const fetchInvoices = useCallback(async () => {
        if (!patientId) return;
        
        setLoading(true);
        try {
            const response = await drEasyApi.get('/api/Invoice/GetUserAccount', {
                params: {
                    UserId: patientId,
                    PageNumber: pagination.current,
                    PageSize: pagination.pageSize,
                    IsPaid: isPaidFilter
                }
            });
            
            setInvoices(response.data.items || []);
            setPagination(prev => ({
                ...prev,
                total: response.data.totalCount || 0
            }));
        } catch (error) {
            console.error('Error fetching invoices:', error);
            message.error('Failed to load invoice history');
        } finally {
            setLoading(false);
        }
    }, [patientId, pagination.current, pagination.pageSize, isPaidFilter]);

    useEffect(() => {
        if (visible && patientId) {
            fetchInvoices();
        }
    }, [visible, patientId, fetchInvoices]);

    // Handle pay invoice
    const handlePayClick = useCallback((invoice) => {
        setCurrentInvoice(invoice);
        const remainingAmount = invoice.totalPrice - invoice.paidAmount;
        payForm.setFieldsValue({
            paidAmount: remainingAmount > 0 ? remainingAmount : 0
        });
        setPayModalVisible(true);
    }, [payForm]);

    const handlePaySubmit = useCallback(async () => {
        try {
            const values = await payForm.validateFields();
            setPaying(true);

            await drEasyApi.post('/api/Invoice/Pay', {
                invoiceId: currentInvoice.id,
                paidAmount: values.paidAmount
            });

            message.success('Payment processed successfully!');
            setPayModalVisible(false);
            payForm.resetFields();
            fetchInvoices();
            
            if (onPayInvoice) {
                onPayInvoice(currentInvoice);
            }
        } catch (error) {
            console.error('Error processing payment:', error);
            message.error(error.response?.data?.message || 'Failed to process payment');
        } finally {
            setPaying(false);
        }
    }, [currentInvoice, payForm, fetchInvoices, onPayInvoice]);

    // Handle edit invoice
    const handleEditClick = useCallback((invoice) => {
        if (onEditInvoice) {
            onEditInvoice(invoice);
            onCancel();
        }
    }, [onEditInvoice, onCancel]);

    // Handle view details
    const handleViewDetails = useCallback((invoice) => {
        setSelectedInvoiceDetails(invoice);
        setDetailsDrawerVisible(true);
    }, []);

    // Table columns
    const columns = [
        {
            title: 'Invoice ID',
            dataIndex: 'id',
            key: 'id',
            width: isMobile ? 100 : 120,
            render: (id) => (
                <Tooltip title={id}>
                    <span className="text-xs font-mono">
                        #{id.slice(0, 8)}...
                    </span>
                </Tooltip>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: isMobile ? 100 : 120,
            render: (date) => dayjs(date).format('MMM D, YYYY'),
        },
        {
            title: 'Visit Price',
            dataIndex: 'visitPrice',
            key: 'visitPrice',
            width: 100,
            render: (price) => (
                <span className="font-semibold text-blue-600">
                    ${price?.toFixed(2) || '0.00'}
                </span>
            ),
        },
    {
    title: 'Total',
    key: 'totals',
    width: 140,
    render: (_, record) => (        // ← use render with record instead of dataIndex
        <span className="font-bold text-green-600">
            EGP {Number(record.totals?.totalAmount ?? 0).toFixed(2)}
        </span>
    ),
},
        {
            title: 'Paid',
            dataIndex: 'paidAmount',
            key: 'paidAmount',
            width: 100,
            render: (paid) => (
                <span className="font-semibold text-emerald-600">
                    ${paid?.toFixed(2) || '0.00'}
                </span>
            ),
        },
       {
    title: 'Remaining',
    key: 'remaining',
    width: 100,
    render: (_, record) => {
        const remaining = Math.max(0, record.remainingAmount ?? 0);  // ← clamp at 0
        return (
            <span className={`font-semibold ${remaining > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                EGP {remaining.toFixed(2)}
            </span>
        );
    },
},
        {
            title: 'Status',
            dataIndex: 'isPaid',
            key: 'isPaid',
            width: 100,
            render: (isPaid) => (
                <Tag 
                    color={isPaid ? 'green' : 'orange'}
                    icon={isPaid ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                >
                    {isPaid ? 'Paid' : 'Unpaid'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: isMobile ? 120 : 150,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="View Details">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => handleViewDetails(record)}
                            size="small"
                            className="text-blue-500 hover:text-blue-700"
                        />
                    </Tooltip>
                    
                    {!record.isPaid && (
                        <Tooltip title="Pay Invoice">
                            <Button
                                type="text"
                                icon={<DollarOutlined />}
                                onClick={() => handlePayClick(record)}
                                size="small"
                                className="text-green-500 hover:text-green-700"
                            />
                        </Tooltip>
                    )}
                    
                    <Tooltip title="Edit Invoice">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditClick(record)}
                            size="small"
                            className="text-orange-500 hover:text-orange-700"
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    // Mobile card view
    const renderMobileCard = (invoice) => {
        const remaining = (invoice.totalPrice || 0) - (invoice.paidAmount || 0);
        
        return (
            <div key={invoice.id} className="bg-white rounded-lg p-4 mb-3 shadow-sm border border-gray-200">
                <div className="flex justify-between items-start mb-3">
                    <div>
                        <div className="text-xs text-gray-500 mb-1">
                            Invoice #{invoice.id.slice(0, 8)}
                        </div>
                        <div className="text-sm font-medium text-gray-700">
                            {dayjs(invoice.createdAt).format('MMM D, YYYY')}
                        </div>
                    </div>
                    <Tag 
                        color={invoice.isPaid ? 'green' : 'orange'}
                        icon={invoice.isPaid ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                        className="ml-2"
                    >
                        {invoice.isPaid ? 'Paid' : 'Unpaid'}
                    </Tag>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                    <div>
                        <div className="text-gray-500 text-xs">Visit Price</div>
                        <div className="font-semibold text-blue-600">
                            ${invoice.visitPrice?.toFixed(2) || '0.00'}
                        </div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs">Total</div>
                        <div className="font-bold text-green-600">
                            ${invoice.totalPrice?.toFixed(2) || '0.00'}
                        </div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs">Paid</div>
                        <div className="font-semibold text-emerald-600">
                            ${invoice.paidAmount?.toFixed(2) || '0.00'}
                        </div>
                    </div>
                    <div>
                        <div className="text-gray-500 text-xs">Remaining</div>
                        <div className={`font-semibold ${remaining > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                            ${remaining.toFixed(2)}
                        </div>
                    </div>
                </div>

                <div className="flex gap-2 pt-3 border-t border-gray-100">
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetails(invoice)}
                        className="flex-1"
                    >
                        View
                    </Button>
                    {!invoice.isPaid && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<DollarOutlined />}
                            onClick={() => handlePayClick(invoice)}
                            className="flex-1 bg-green-500 border-green-500"
                        >
                            Pay
                        </Button>
                    )}
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditClick(invoice)}
                        className="flex-1"
                    >
                        Edit
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <>
            <Modal
                title={
                    <div className="flex items-center">
                        <div className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center ${isMobile ? 'mr-2' : 'mr-3'}`}>
                            <FaFileInvoiceDollar className={`text-white ${isMobile ? 'text-xs' : 'text-sm'}`} />
                        </div>
                        <div>
                            <span className={isMobile ? 'text-sm' : ''}>Invoice History</span>
                            {patientName && (
                                <div className="text-xs text-gray-500 font-normal">
                                    Patient: {patientName}
                                </div>
                            )}
                        </div>
                    </div>
                }
                open={visible}
                onCancel={onCancel}
                footer={[
                    <Button key="close" onClick={onCancel}>
                        Close
                    </Button>
                ]}
                width={isMobile ? '95vw' : 1000}
                className="modern-modal"
                centered={isMobile}
            >
                <Spin spinning={loading}>
                    {/* Filter buttons */}
                    <div className="mb-4 flex gap-2 flex-wrap">
                        <Button
                            size="small"
                            type={isPaidFilter === undefined ? 'primary' : 'default'}
                            onClick={() => {
                                setIsPaidFilter(undefined);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            className={isPaidFilter === undefined ? 'bg-primary' : ''}
                        >
                            All
                        </Button>
                        <Button
                            size="small"
                            type={isPaidFilter === true ? 'primary' : 'default'}
                            onClick={() => {
                                setIsPaidFilter(true);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            className={isPaidFilter === true ? 'bg-green-500 border-green-500' : ''}
                        >
                            Paid
                        </Button>
                        <Button
                            size="small"
                            type={isPaidFilter === false ? 'primary' : 'default'}
                            onClick={() => {
                                setIsPaidFilter(false);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            className={isPaidFilter === false ? 'bg-orange-500 border-orange-500' : ''}
                        >
                            Unpaid
                        </Button>
                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={fetchInvoices}
                        >
                            Refresh
                        </Button>
                    </div>

                    {/* Table or cards view */}
                    {isMobile ? (
                        <div className="max-h-96 overflow-y-auto">
                            {invoices.length > 0 ? (
                                invoices.map(renderMobileCard)
                            ) : (
                                <div className="text-center py-8 text-gray-500">
                                    <FaFileInvoiceDollar className="text-4xl mx-auto mb-2 opacity-50" />
                                    <div>No invoices found</div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={invoices}
                            rowKey="id"
                            pagination={{
                                ...pagination,
                                onChange: (page, pageSize) => {
                                    setPagination({ ...pagination, current: page, pageSize });
                                },
                                showSizeChanger: true,
                                showTotal: (total) => `Total ${total} invoices`,
                            }}
                            scroll={{ x: 900 }}
                            size="small"
                        />
                    )}
                </Spin>
            </Modal>

            {/* Pay Invoice Modal */}
            <Modal
                title={
                    <div className="flex items-center">
                        <div className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center ${isMobile ? 'mr-2' : 'mr-3'}`}>
                            <FaMoneyBillWave className={`text-white ${isMobile ? 'text-xs' : 'text-sm'}`} />
                        </div>
                        <span className={isMobile ? 'text-sm' : ''}>Pay Invoice</span>
                    </div>
                }
                open={payModalVisible}
                onCancel={() => {
                    setPayModalVisible(false);
                    payForm.resetFields();
                }}
                footer={[
                    <Button 
                        key="cancel" 
                        onClick={() => {
                            setPayModalVisible(false);
                            payForm.resetFields();
                        }}
                    >
                        Cancel
                    </Button>,
                    <Button 
                        key="submit" 
                        type="primary" 
                        onClick={handlePaySubmit}
                        loading={paying}
                        icon={<CheckOutlined />}
                        className="bg-green-500 border-green-500"
                    >
                        Process Payment
                    </Button>
                ]}
                width={isMobile ? '95vw' : 500}
                centered={isMobile}
            >
                {currentInvoice && (
                    <div>
                        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-sm space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total Amount:</span>
                                    <span className="font-bold text-blue-600">
                                        ${currentInvoice.totalPrice?.toFixed(2) || '0.00'}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Already Paid:</span>
                                    <span className="font-semibold text-green-600">
                                        ${currentInvoice.paidAmount?.toFixed(2) || '0.00'}
                                    </span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-blue-300">
                                    <span className="text-gray-700 font-medium">Remaining:</span>
                                    <span className="font-bold text-red-600">
                                        ${((currentInvoice.totalPrice || 0) - (currentInvoice.paidAmount || 0)).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <Form form={payForm} layout="vertical">
                            <Form.Item
                                name="paidAmount"
                                label={<span className={isMobile ? 'text-sm' : ''}>Payment Amount</span>}
                                rules={[
                                    { required: true, message: 'Please enter payment amount' },
                                    { type: 'number', min: 0.01, message: 'Amount must be greater than 0' },
                                 
                                ]}
                            >
                                <InputNumber
                                    placeholder="Enter payment amount"
                                    className="w-full rounded-lg"
                                    size={isMobile ? 'middle' : 'large'}
                                    prefix="EGP"
                                    min={0}
                                    step={0.01}
                                    precision={2}
                                />
                            </Form.Item>
                        </Form>
                    </div>
                )}
            </Modal>

            {/* Invoice Details Drawer */}
            <Drawer
                title="Invoice Details"
                placement={isMobile ? 'bottom' : 'right'}
                onClose={() => setDetailsDrawerVisible(false)}
                open={detailsDrawerVisible}
                width={isMobile ? '100%' : 500}
                height={isMobile ? '85%' : undefined}
            >
                {selectedInvoiceDetails && (
                    <div className="space-y-4">
                        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                            <h3 className="font-bold text-lg mb-2">Invoice #{selectedInvoiceDetails.id.slice(0, 8)}</h3>
                            <div className="text-sm text-gray-600">
                                Created: {dayjs(selectedInvoiceDetails.createdAt).format('MMMM D, YYYY h:mm A')}
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                <span className="text-gray-600">Visit Price</span>
                                <span className="font-semibold text-blue-600">
                                    ${selectedInvoiceDetails.visitPrice?.toFixed(2) || '0.00'}
                                </span>
                            </div>

                            {selectedInvoiceDetails.discountAmount > 0 && (
                                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                                    <span className="text-gray-600">Discount</span>
                                    <span className="font-semibold text-red-600">
                                        -${selectedInvoiceDetails.discountAmount?.toFixed(2) || '0.00'}
                                    </span>
                                </div>
                            )}

                            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                                <span className="text-gray-700 font-medium">Total Amount</span>
                                <span className="font-bold text-green-600 text-lg">
                                    ${selectedInvoiceDetails.totalPrice?.toFixed(2) || '0.00'}
                                </span>
                            </div>

                            <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                                <span className="text-gray-600">Paid Amount</span>
                                <span className="font-semibold text-emerald-600">
                                    ${selectedInvoiceDetails.paidAmount?.toFixed(2) || '0.00'}
                                </span>
                            </div>

                            <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg border-2 border-orange-200">
                                <span className="text-gray-700 font-medium">Remaining Balance</span>
                                <span className="font-bold text-orange-600 text-lg">
                                    ${((selectedInvoiceDetails.totalPrice || 0) - (selectedInvoiceDetails.paidAmount || 0)).toFixed(2)}
                                </span>
                            </div>
                        </div>

                        {selectedInvoiceDetails.services && selectedInvoiceDetails.services.length > 0 && (
                            <div className="mt-6">
                                <h4 className="font-semibold mb-3 text-gray-800">Services Included</h4>
                                <div className="space-y-2">
                                    {selectedInvoiceDetails.services.map((service, index) => (
                                        <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                                            {service.photoID && (
                                                <img
                                                    src={getImg(service.photoID)}
                                                    className="w-12 h-12 rounded-lg object-cover"
                                                    alt={service.title}
                                                />
                                            )}
                                            <div className="flex-1">
                                                <div className="font-medium text-gray-800">{service.title}</div>
                                                {service.price >= 0 && (
                                                    <div className="text-sm text-green-600 font-semibold">
                                                        ${service.price?.toFixed(2)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="pt-4 flex gap-2">
                            {!selectedInvoiceDetails.isPaid && (
                                <Button
                                    type="primary"
                                    icon={<DollarOutlined />}
                                    onClick={() => {
                                        setDetailsDrawerVisible(false);
                                        handlePayClick(selectedInvoiceDetails);
                                    }}
                                    className="flex-1 bg-green-500 border-green-500"
                                >
                                    Pay Invoice
                                </Button>
                            )}
                            <Button
                                icon={<EditOutlined />}
                                onClick={() => {
                                    setDetailsDrawerVisible(false);
                                    handleEditClick(selectedInvoiceDetails);
                                }}
                                className="flex-1"
                            >
                                Edit Invoice
                            </Button>
                        </div>
                    </div>
                )}
            </Drawer>
        </>
    );
};

export default PatientInvoiceHistoryModal;